import React, { FC, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import OnboardingShell from "./OnboardingShell";
import DocumentDropzone, { MAX_SIZE_MB } from "./DocumentDropzone";
import { useLocale } from "./i18n/locale";

export interface TenantVerifyIdentityPageProps {
  className?: string;
  /** 4 for Flow A (short onboarding), 7 for Flow B (full onboarding). */
  totalSteps?: number;
}

/**
 * ID card with an upload badge, matching the spec mockup: a header rule, the
 * portrait as head-and-bust, detail lines beside it, two lines along the foot,
 * and the arrow badge punching a gap through the card's corner.
 */
const IdDocumentIcon = () => (
  <svg
    className="h-24 w-24 text-navy-800"
    viewBox="0 0 60 48"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="1" y="4" width="45" height="33" rx="3" />
    <path d="M5 10h37" />

    <circle cx="16" cy="19.5" r="4.2" />
    <path d="M10.5 29.5c0-3 2.5-5.2 5.5-5.2s5.5 2.2 5.5 5.2z" />

    <path d="M27 16.5h11M27 20.5h9.5M27 24.5h6.5" />
    <path d="M5 31.5h31M5 35h23" />

    {/* Filled with the dropzone's own background so the badge reads as a hole
        punched through the card, as in the design. */}
    <circle cx="45" cy="34" r="9.5" fill="#FFFFFF" strokeWidth={1.5} />
    <path d="M45 38.6v-9.2m0 0l-3.4 3.4m3.4-3.4l3.4 3.4" />
  </svg>
);

const TenantVerifyIdentityPage: FC<TenantVerifyIdentityPageProps> = ({
  className = "",
  totalSteps = 7,
}) => {
  const navigate = useNavigate();
  const { t } = useLocale();
  const [file, setFile] = useState<File | null>(null);

  const handleSubmit = () => {
    if (!file) return;
    // TODO: upload to the verification endpoint before advancing.
    navigate("/tenant/sign-contract");
  };

  return (
    <div
      className={`nc-TenantVerifyIdentityPage ${className}`}
      data-nc-id="TenantVerifyIdentityPage"
    >
      <Helmet>
        <title>{t.verify.documentTitle}</title>
      </Helmet>

      <OnboardingShell
        step={2}
        totalSteps={totalSteps}
        title={t.verify.title}
        subtitle={t.verify.subtitle}
        footnote={
          <>
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
            {t.verify.footnote}
          </>
        }
      >
        <div className="flex items-center gap-3 rounded-md border border-primary-6000/20 bg-cream-100 px-4 py-3">
          {/* Padlock with a keyhole, as drawn in the spec mockup. */}
          <span className="mt-0.5 flex flex-none items-center justify-center text-primary-6000">
            <svg
              className="h-10 w-10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.4}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="4.5" y="10" width="15" height="11" rx="2.5" />
              <path d="M8 10V7.5a4 4 0 018 0V10" />
              <circle cx="12" cy="14.8" r="1.3" />
              <path d="M12 16.1v2.4" />
            </svg>
          </span>
          <p className="text-[13px] leading-relaxed text-neutral-600 font-semibold">
            {t.verify.notice}
          </p>
        </div>

        <DocumentDropzone
          id="id-document"
          className="mt-4"
          icon={<IdDocumentIcon />}
          prompt={
            <>
              {t.verify.promptLine1}
              <br />
              {t.verify.promptLine2}
            </>
          }
          hint={
            <>
              <span className="font-semibold">{t.verify.hintLine1}</span>

              <br />
              <span className="font-semibold">{t.verify.hintLine2(MAX_SIZE_MB)}</span>
            </>
          }
          file={file}
          onFileChange={setFile}
        />

        <button
          type="button"
          onClick={handleSubmit}
          disabled={!file}
          className="mt-4 flex w-full items-center justify-between gap-3 rounded-md bg-navy-800 px-6 py-4 text-sm font-semibold tracking-wide text-white transition-colors hover:bg-navy-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-800 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <span className="flex-1 text-center">{t.verify.cta}</span>
          <svg
            className="h-5 w-5 flex-none"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M5 12h18m0 0l-7-7m7 7l-7 7" />
          </svg>
        </button>
      </OnboardingShell>
    </div>
  );
};

export default TenantVerifyIdentityPage;
